import type { ItemFieldRow } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { utf8Bytes } from './php-json-serialize';
import { phpStableSort } from './php-stable-sort';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const trim = (value: string): string => value.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+(?![\s\S])/g,'');
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
export function normalizeInventoryPictureUuid(input: unknown): string | null {
    const value = arrayLike(input) ? own(input,'0') : input;
    if (typeof value !== 'string') return null;
    const id = trim(value);
    return /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(id) ? id.toUpperCase() : null;
}
export interface InventoryTemplatePictureReaders {
    itemField: (row: ItemFieldRow | null,key: string) => unknown;
    fetchPictures: (filters: Readonly<Record<string,unknown>>) => unknown;
    invalidIteration?: () => void;
}
/** A raw PHP byte-offset filter cannot be transported as UTF-8 JSON. */
export class InventoryPictureQueryEncodingError extends Error {}
const legacyLayers = ['weapon_classification','weapon_ammo_classification','armor_classification','armor_type','lock_type','item_composition'];
/** Actual collection helper: root list, base identity queries, then legacy [0]
 * classification fields. This differs from the constructor's later layer-aware loop. */
export function collectInventoryTemplatePictures(row: ItemFieldRow,readers: InventoryTemplatePictureReaders): ItemFieldRow {
    if (!arrayLike(row)) throw new TypeError('Template picture collection requires a PHP array');
    const stored = own(row,'item_picture') ?? [];
    if (phpJsonTruthy(stored)) {
        if (!arrayLike(stored)) throw new TypeError('array_filter requires a PHP array');
        const pictures = Object.values(stored).filter(arrayLike);
        if (pictures.length) return clone(pictures) as ItemFieldRow;
    }
    const fetch = (filters: Readonly<Record<string,unknown>>): ItemFieldRow | null => {
        const result = readers.fetchPictures(filters);
        if (!phpJsonTruthy(result)) return null;
        if (!arrayLike(result)) throw new TypeError('Picture collector return requires a PHP array');
        return clone(result) as ItemFieldRow;
    };
    const base = readInventoryField(row,'inventory_item_id') ?? readers.itemField(row,'item_id') ?? null;
    if (normalizeInventoryPictureUuid(base) !== null) {
        const attached = typeof base === 'string' ? trim(base) : phpJsonString(base);
        const typed = fetch({picture_deleted:0,attached_id:attached,attached_item_type:'item'});
        if (typed !== null) return typed;
        const plain = fetch({picture_deleted:0,attached_id:attached});
        if (plain !== null) return plain;
    }
    for (const key of legacyLayers) {
        const slot = own(row,key);
        // PHP root[key][0] on strings is the first UTF-8 byte, even for a UUID.
        const id = typeof slot === 'string' ? (utf8Bytes(slot)[0] === undefined ? undefined : String.fromCharCode(utf8Bytes(slot)[0]!)) : own(slot,'0');
        if (!phpJsonTruthy(id)) continue;
        if (typeof slot === 'string' && typeof id === 'string' && id.charCodeAt(0) >= 128) throw new InventoryPictureQueryEncodingError();
        const pictures = fetch({picture_deleted:0,attached_id:id});
        if (pictures !== null) return pictures;
    }
    return [];
}
const rowPictureId = (row: ItemFieldRow): string | null => {
    const id = own(row,'picture_id');
    return normalizeInventoryPictureUuid(arrayLike(id) ? own(id,'0') ?? null : id);
};
export function pickStableInventoryTemplatePicture(row: ItemFieldRow,readers: InventoryTemplatePictureReaders): string | null {
    let pictures = Object.values(collectInventoryTemplatePictures(row,readers));
    if (!pictures.length) return null;
    pictures = phpStableSort(pictures,(left,right)=>{
        if (!arrayLike(left) || !arrayLike(right)) return 0;
        const a = rowPictureId(left) ?? '',b = rowPictureId(right) ?? '';
        return a < b ? -1 : a > b ? 1 : 0;
    });
    return arrayLike(pictures[0]) ? rowPictureId(pictures[0]) : null;
}
/** The inventory constructor always requests stable_when_multiple=true. Random
 * new-item authoring is a separate workflow and is not exposed by this compiler. */
export function resolveInventoryTemplatePicture(row: ItemFieldRow,readers: InventoryTemplatePictureReaders): string | null {
    const selected = pickStableInventoryTemplatePicture(row,readers);
    if (selected !== null) return selected;
    const rows = own(row,'item_picture') ?? [];
    if (!arrayLike(rows)) { readers.invalidIteration?.(); return null; }
    for (const entry of Object.values(rows)) {
        if (!arrayLike(entry)) continue;
        const raw = own(entry,'picture_id');
        if (raw != null) { const id = normalizeInventoryPictureUuid(raw); if (id !== null) return id; }
        for (const cell of Object.values(entry)) {
            if (!arrayLike(cell) || own(cell,'picture_id') == null) continue;
            const id = rowPictureId(cell);
            if (id !== null) return id;
        }
    }
    return null;
}
export function createInventoryTemplatePictureCompiler(deps: FormulaReadDependencies & Pick<InventoryTemplatePictureReaders,'fetchPictures' | 'invalidIteration'>): {
    collect: (row: ItemFieldRow) => ItemFieldRow;
    stable: (row: ItemFieldRow) => string | null;
    resolve: (row: ItemFieldRow) => string | null;
} {
    const context = createFormulaReadContext(deps),readers = {...deps,itemField:context.itemField};
    return {collect:row=>collectInventoryTemplatePictures(row,readers),stable:row=>pickStableInventoryTemplatePicture(row,readers),resolve:row=>resolveInventoryTemplatePicture(row,readers)};
}
